import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Lock, KeyRound, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, extractError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { ADMIN_AREAS } from "@/components/layout/adminNavMap";

// Lo sblocco vive solo in memoria: niente ricordo di account o dispositivo.
// Appena si esce dalle pagine riservate (o si ricarica/chiude) il codice va rimesso.
let _token = null;

export const LOCKED_PATHS = ADMIN_AREAS.flatMap((a) => a.groups.flatMap((g) => g.pages.filter((p) => p.lock && p.to).map((p) => p.to)));
export const isLockedPath = (pathname) => LOCKED_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"));

export function hasAdminCode() { return !!_token; }
export function getAdminCode() { return _token; }
export function storeAdminCode(token) { _token = token; window.dispatchEvent(new Event("admin-code-changed")); }
export function clearAdminCode() { _token = null; window.dispatchEvent(new Event("admin-code-changed")); }

function CodeForm({ title, hint, onSubmit, busy, error, confirm, askCurrent, submitLabel, children }) {
  const [current, setCurrent] = useState("");
  const [code, setCode] = useState("");
  const [code2, setCode2] = useState("");
  const mismatch = confirm && code2 && code !== code2;
  return (
    <form
      data-testid="admin-code-form"
      onSubmit={(e) => { e.preventDefault(); if (!mismatch) onSubmit(code, current); }}
      className="w-full max-w-sm bg-white border border-gray-200 rounded-2xl shadow-sm p-6 space-y-4"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#1F1F1F] text-white flex items-center justify-center"><Lock className="w-5 h-5" /></div>
        <div>
          <div className="font-black text-lg leading-tight">{title}</div>
          <div className="text-xs text-gray-500">{hint}</div>
        </div>
      </div>
      {askCurrent && (
        <Input data-testid="admin-code-current-input" type="password" autoFocus autoComplete="off" placeholder="Codice attuale oppure il tuo PIN super admin"
          value={current} onChange={(e) => setCurrent(e.target.value)} />
      )}
      <Input data-testid="admin-code-input" type="password" autoFocus={!askCurrent} inputMode="numeric" autoComplete="off"
        placeholder={confirm ? "Nuovo codice" : "Codice di accesso"} value={code} onChange={(e) => setCode(e.target.value)} />
      {confirm && (
        <Input data-testid="admin-code-confirm-input" type="password" autoComplete="off" placeholder="Ripeti il codice"
          value={code2} onChange={(e) => setCode2(e.target.value)} />
      )}
      {mismatch && <div className="text-xs text-red-600">I due codici non coincidono.</div>}
      {error && <div data-testid="admin-code-error" className="text-sm text-red-600">{error}</div>}
      <Button data-testid="admin-code-submit" type="submit" disabled={busy || code.length < 4 || !!mismatch} className="w-full bg-[#1F1F1F] hover:bg-black">
        <KeyRound className="w-4 h-4 mr-2" />{submitLabel}
      </Button>
      {children}
    </form>
  );
}

// Protegge fatture / clienti / dipendenti: serve il codice di accesso del super admin, ogni volta.
export function AdminCodeGate({ children }) {
  const { user } = useAuth();
  const [unlocked, setUnlocked] = useState(hasAdminCode());
  const [resetting, setResetting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const status = useQuery({ queryKey: ["admin-code-status"], queryFn: async () => (await api.get("/auth/admin-code")).data, enabled: !unlocked });

  useEffect(() => {
    const sync = () => setUnlocked(hasAdminCode());
    window.addEventListener("admin-code-required", sync);
    window.addEventListener("admin-code-changed", sync);
    return () => { window.removeEventListener("admin-code-required", sync); window.removeEventListener("admin-code-changed", sync); };
  }, []);

  if (unlocked) return children;
  if (status.isLoading) return <div className="p-8 text-gray-500">Caricamento…</div>;

  const isRealSuper = user?.role === "super_admin" && !user?.actual_role;
  const configured = !!status.data?.configured;

  const submit = async (code, current) => {
    setBusy(true); setError("");
    try {
      const r = configured && !resetting
        ? await api.post("/auth/admin-code/verify", { code })
        : await api.put("/auth/admin-code", { code, current_code: current || null, pin: current || null });
      if (resetting) toast.success("Codice reimpostato");
      storeAdminCode(r.data.admin_code_token);
      setResetting(false);
      status.refetch();
    } catch (e) { setError(extractError(e)); } finally { setBusy(false); }
  };

  if (!configured && !isRealSuper) {
    return (
      <div data-testid="admin-code-locked" className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="max-w-sm text-center space-y-2">
          <Lock className="w-8 h-8 mx-auto text-gray-400" />
          <div className="font-black text-lg">Area riservata al super admin</div>
          <div className="text-sm text-gray-500">Il codice di accesso non è ancora stato impostato dal super admin.</div>
        </div>
      </div>
    );
  }

  const setting = !configured || resetting;
  return (
    <div data-testid="admin-code-gate" className="min-h-[60vh] flex items-center justify-center p-6">
      <CodeForm
        key={setting ? "set" : "verify"}
        confirm={setting}
        askCurrent={resetting}
        title={resetting ? "Reimposta il codice" : configured ? "Area riservata" : "Imposta il codice super admin"}
        hint={resetting ? "Codice dimenticato? Basta il tuo PIN di accesso. Poi scegli il nuovo codice." : configured ? "Fatture, clienti e dipendenti: inserisci il codice del super admin." : "Proteggerà fatture, clienti e dipendenti. Minimo 4 caratteri."}
        submitLabel={resetting ? "Salva nuovo codice" : setting ? "Imposta codice" : "Sblocca"}
        onSubmit={submit} busy={busy} error={error}
      >
        {configured && isRealSuper && (
          <button type="button" data-testid="admin-code-reset-toggle" onClick={() => { setResetting((v) => !v); setError(""); }}
            className="w-full text-xs text-gray-500 hover:text-[#1F1F1F] flex items-center justify-center gap-1 pt-1">
            <RotateCcw className="w-3 h-3" /> {resetting ? "Annulla, torna allo sblocco" : "Reimposta il codice"}
          </button>
        )}
      </CodeForm>
    </div>
  );
}
